import React, {
  KeyboardEvent,
  MouseEvent,
  RefObject,
  ReactNode
} from 'react';
import SvgIcon from 'src/react_components/svg-icon';
import SourcesInfoTooltip from 'annotator/sources-info-tooltip';
import { AnnotatorObject } from './annotator-types';
import handleNavigateBackToSentenceHighlight from './comment-navigation';

declare const Annotator: AnnotatorObject;

type StudentCommentInputProps = {
  node: RefObject<HTMLDivElement>,
  wrapperClass: string,
  timestamp: string,
  selectHighlight: () => void,
  keyboardSelectHighlight: (e: KeyboardEvent) => void,
  hoverHighlight: (e: MouseEvent) => void,
  dehoverHighlights: (e: MouseEvent) => void,
  sources: number[],
  comment: string,
  commentClasses: string,
  commentIcon: string,
  showSourcesInfoTooltip: string,
  isSelected: boolean,
  isHighlighted: boolean,
  commentType: string,
  children: ReactNode
}

const StudentCommentInput = ({
  node,
  wrapperClass,
  timestamp,
  selectHighlight,
  keyboardSelectHighlight,
  hoverHighlight,
  dehoverHighlights,
  sources,
  comment,
  commentClasses,
  commentIcon,
  showSourcesInfoTooltip,
  isSelected,
  isHighlighted,
  commentType,
  children
}: StudentCommentInputProps) => {
  const commentCollapseElement = !Annotator.portFairyEnabled && (
    <React.Fragment>
      {sources && (
        <SvgIcon
          className="icon-sm mb-0 icon-comment-category-use-of-sources"
          icon="comment-category-use-of-sources"
        />
      )}
      {comment && !sources && ( // only show for comments without citations
        <svg className={commentClasses}>
          <use xlinkHref={`#${commentIcon}`} />
        </svg>
      )}
    </React.Fragment>
  );

  return (
    <div
      role="listitem"
      className={['outer-panel visible-outer-panel', isSelected && 'outer-panel-active'].filter(Boolean).join(' ')}
      ref={node}
    >
      {/*
        Outer div provides large click area for mouse users. Keyboard accessibility handled by
        inner button to avoid nested buttons. Once selected and highlighted, body clicks are
        ignored so interactive content in children can't collapse the card out from under it.
      */}
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <div
        className={wrapperClass}
        data-timestamp={timestamp}
        onClick={isSelected && isHighlighted ? undefined : selectHighlight}
        onMouseEnter={hoverHighlight}
        onMouseLeave={dehoverHighlights}
      >
        <div
          className="panel-icons-container"
          role="button"
          tabIndex={commentCollapseElement ? 0 : -1}
          aria-hidden={!commentCollapseElement}
          aria-expanded={isSelected}
          aria-label={commentType}
          onClick={(e) => {
            // Stops this bubbling to the wrapper above (also toggles on collapse) and double-firing selectHighlight.
            e.stopPropagation();
            selectHighlight();
          }}
          onKeyUp={keyboardSelectHighlight}
        >
          {commentCollapseElement}
          {showSourcesInfoTooltip && <SourcesInfoTooltip />}
        </div>
        <button
          type="button"
          aria-label="Go to document"
          className="sr-only-show-on-focus"
          tabIndex={isSelected ? 0 : -1}
          onClick={(e) => {
            e.stopPropagation();
            handleNavigateBackToSentenceHighlight(timestamp);
          }}
        >
          <SvgIcon icon="navigate_before" />
        </button>
        {children}
      </div>
    </div>
  );
};

export default StudentCommentInput;
